import { Badge } from '@/components/ui/badge';
import type { SessionDetail } from '@/lib/api/user-activity-details';
import type { SessionType } from '@/lib/api/user-activity-details';
import { formatDistanceToNow } from 'date-fns';
import { Bot, FileText, ArrowRightCircle, ListTodo, Hammer } from 'lucide-react';

const sessionTypeConfig: Record<SessionType, { label: string; icon: React.ReactNode; color: string }> = {
  explicit: { label: 'AI Session', icon: <Bot className="h-4 w-4" />, color: 'text-blue-500' },
  er_creation: { label: 'Epic Request Created', icon: <FileText className="h-4 w-4" />, color: 'text-green-500' },
  er_conversion: { label: 'Epic Request Converted', icon: <ArrowRightCircle className="h-4 w-4" />, color: 'text-purple-500' },
  planning: { label: 'Planning Session', icon: <ListTodo className="h-4 w-4" />, color: 'text-amber-500' },
  implementation: { label: 'Implementation Session', icon: <Hammer className="h-4 w-4" />, color: 'text-orange-500' },
};

interface SessionDetailListProps {
  items: SessionDetail[];
}

/**
 * SessionDetailList - Renders a list of AI sessions with metadata
 * 
 * Shows:
 * - Epic name
 * - Session duration (calculated or "In progress")
 * - Session summary (if available)
 * - Status badge (colored by status)
 * - Start time (relative)
 */
export function SessionDetailList({ items }: SessionDetailListProps) {
  return (
    <ul className="space-y-0" role="list">
      {items.map((item) => (
        <li key={item.id}>
          <SessionDetailItem item={item} />
        </li>
      ))}
    </ul>
  );
}

function SessionDetailItem({ item }: { item: SessionDetail }) {
  const sessionType = item.sessionType ?? 'explicit';
  const config = sessionTypeConfig[sessionType] ?? sessionTypeConfig.explicit;
  const isInferred = sessionType !== 'explicit';

  const getDuration = () => {
    if (!item.endedAt) {
      return 'In progress';
    }
    const start = new Date(item.startedAt);
    const end = new Date(item.endedAt);
    const durationMs = end.getTime() - start.getTime();
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${String(hours)}h ${String(minutes)}m`;
    }
    return `${String(minutes)}m`;
  };

  const getStatusBadgeClass = () => {
    switch (item.status) {
      case 'active':
        return 'bg-green-900/30 text-green-300';
      case 'completed':
        return 'bg-blue-900/30 text-blue-300';
      case 'abandoned':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const duration = getDuration();
  const primaryText = isInferred && item.entityTitle ? item.entityTitle : item.epicName;
  const ariaLabel = `${config.label} for ${primaryText}. Duration: ${duration}. Status: ${item.status}. Started ${formatDistanceToNow(new Date(item.startedAt), { addSuffix: true })}${item.summary ? `. ${item.summary}` : ''}`;

  return (
    <article className="border-b py-3 px-3 sm:px-4" aria-label={ariaLabel}>
      <div className="space-y-2">
        <div className="flex items-start sm:items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className={`flex-shrink-0 ${config.color}`} aria-hidden="true">{config.icon}</span>
            <span className="font-medium text-sm sm:text-base line-clamp-2 flex-1">{primaryText}</span>
          </div>
          <span className="text-xs sm:text-sm text-muted-foreground flex-shrink-0 whitespace-nowrap" aria-hidden="true">{duration}</span>
        </div>
        {isInferred && item.epicName && primaryText !== item.epicName && (
          <p className="text-xs text-muted-foreground ml-6" aria-hidden="true">{config.label} · {item.epicName}</p>
        )}
        {item.summary && (
          <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2 ml-6" aria-hidden="true">{item.summary}</p>
        )}
        <div className="flex items-center gap-1.5 sm:gap-2 text-xs flex-wrap ml-6" aria-hidden="true">
          <Badge className={`text-xs border-0 flex-shrink-0 ${getStatusBadgeClass()}`}>
            {item.status}
          </Badge>
          <span className="text-muted-foreground whitespace-nowrap">
            {formatDistanceToNow(new Date(item.startedAt), { addSuffix: true })}
          </span>
        </div>
      </div>
    </article>
  );
}
